import {Component, inject, signal, computed, ChangeDetectionStrategy} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_SCENE} from "@common/services/logging"
import {FormsModule} from "@angular/forms"
import {MatDialogRef} from "@angular/material/dialog"
import {MatInputModule} from "@angular/material/input"
import {MutationCreateHdriInput} from "@generated"
import {AddItemDialogComponent} from "@common/components/dialogs/add-item-dialog/add-item-dialog.component"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {StringInputComponent} from "@common/components/inputs/string-input/string-input.component"
import {OrganizationSelectComponent} from "@common/components/inputs/select/organization-select/organization-select.component"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {CreateHdrisGridItemGQL} from "@platform/components/hdris/hdris-grid/hdris-grid.generated"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-add-hdri-dialog",
    imports: [FormsModule, MatInputModule, AddItemDialogComponent, InputContainerComponent, StringInputComponent, OrganizationSelectComponent],
    templateUrl: "./add-hdri-dialog.component.html",
    styleUrl: "./add-hdri-dialog.component.scss",
})
export class AddHdriDialogComponent {
    private dialogRef = inject(MatDialogRef<AddHdriDialogComponent>)
    private createHdriGql = inject(CreateHdrisGridItemGQL)
    readonly organizations = inject(OrganizationsService)
    private readonly logger: CmLogger = inject(CM_LOGGER_SCENE)

    readonly $isLoading = signal(false)

    readonly $data = signal<Omit<MutationCreateHdriInput, "organizationId">>({
        name: "",
    })

    selectedOrganizationId?: string

    updateOrganization = async (data: {organizationId: string}) => {
        this.selectedOrganizationId = data.organizationId
    }

    readonly $isFormValid = computed(() => !!this.$data().name && !!this.selectedOrganizationId)

    async onConfirm() {
        if (!this.$isFormValid()) {
            return
        }

        this.$isLoading.set(true)
        try {
            const result = await mutateThrowingErrors(this.createHdriGql)({
                input: {
                    ...this.$data(),
                    organizationId: this.selectedOrganizationId!,
                },
            })
            this.dialogRef.close(result.createHdri)
        } catch (error) {
            this.logger.error("Failed to create HDRI", error)
            alert(`Failed to create HDRI: ${error}`)
        } finally {
            this.$isLoading.set(false)
        }
    }
}
